<script lang="ts">
	import { devices } from '../../../stores';
	import BaseNode from './BaseNode.svelte';
	import type { FlowNodePort } from '../helpers/flowBranchHandleStyles';
	import { resolveDeviceInStore } from '../helpers/resolveDeviceInStore';
	import type { NodeData } from '../types/flow-types';

	type SwitchCase = {
		id: string;
		value: string;
		label: string;
		operator: string;
		value2?: string;
	};

	export let id: string;
	export let data: NodeData;
	export let selected: boolean = false;
	export let validated: boolean = true;
	export let errors: string[] = [];

	$: cases = (data.cases as SwitchCase[] | undefined) ?? [];
	$: rawProperty = data.property as unknown;
	$: propertyLabelRaw =
		typeof rawProperty === 'string'
			? rawProperty
			: (rawProperty as { label?: string; id?: string } | undefined)?.label ||
				(rawProperty as { id?: string } | undefined)?.id ||
				String(data.sourceLabel || '');
	$: propertyLabel = humanizeProperty(propertyLabelRaw);
	$: selectedChannel = parseInt(String(data.channel ?? '0'), 10) || 0;
	$: resolvedDevice = data.deviceId
		? resolveDeviceInStore($devices, String(data.deviceId), selectedChannel)
		: null;
	$: deviceName = String(data.deviceName || resolvedDevice?.name || '');
	$: sourceLabel =
		deviceName && propertyLabel
			? `${deviceName} › ${propertyLabel}`
			: deviceName || propertyLabel || 'Pick device & property';
	$: hasDeviceSource = Boolean(data.deviceId || String(data.sourceRef || '').trim());
	$: isUnconfigured = !hasDeviceSource || !propertyLabel;
	$: verboseTitle = isUnconfigured ? 'Configure switch' : `Watch ${sourceLabel}`;
	$: effectiveValidated = validated && !isUnconfigured;
	$: inputPorts = [{ id: 'input', color: '#38bdf8' }] satisfies FlowNodePort[];
	const CASE_COLORS = ['#38bdf8', '#a78bfa', '#f59e0b', '#f472b6', '#34d399'];
	const caseColor = (index: number) => CASE_COLORS[index % CASE_COLORS.length];
	$: outputPorts = [
		...cases.map(
			(_, index): FlowNodePort => ({
				id: `case-${index}`,
				color: caseColor(index),
				alignSelector: `[data-switch-port="case-${index}"]`,
			}),
		),
		{
			id: 'default',
			color: '#94a3b8',
			alignSelector: '[data-switch-port="default"]',
		},
	];

	function operatorSymbol(op: string): string {
		switch (op) {
			case 'eq':
				return '=';
			case 'ne':
				return '≠';
			case 'gt':
				return '>';
			case 'ge':
				return '≥';
			case 'lt':
				return '<';
			case 'le':
				return '≤';
			case 'between_inc':
				return '[ ]';
			case 'between_exc':
				return '( )';
			default:
				return '?';
		}
	}

	function humanizeProperty(value: string): string {
		return String(value || '')
			.replace(/([a-zA-Z])(\d+)$/, '$1 $2')
			.replace(/[_-]+/g, ' ')
			.replace(/\b\w/g, (letter) => letter.toUpperCase())
			.trim();
	}

	function caseValueLabel(c: SwitchCase): string {
		const symbol = operatorSymbol(c.operator);
		if (c.operator === 'between_inc' || c.operator === 'between_exc') {
			return `${symbol} ${c.value} – ${c.value2 ?? '?'}`;
		}
		return `${symbol} ${c.value}`;
	}
</script>

<BaseNode
	{id}
	type="switchNode"
	{data}
	{selected}
	validated={effectiveValidated}
	{errors}
	{inputPorts}
	{outputPorts}
	cardTitle={verboseTitle}
	nodeTypeLabel="Switch"
	avatarOverride={{ icon: 'fa-solid fa-shuffle', backgroundColor: 'color-lightblue' }}
	{...$$restProps}
>
	<div class="switch-summary">
		<div class:unconfigured-source={isUnconfigured} class="source-row">
			<i class="fa-solid fa-microchip"></i>
			<span>{sourceLabel}</span>
		</div>

		{#if cases.length}
			<div class="case-list">
				{#each cases as item, index (item.id || index)}
					<div
						class="case-row"
						data-switch-port={`case-${index}`}
						style="--switch-case-color: {caseColor(index)};"
					>
						<span class="case-index">{index + 1}</span>
						<span class="case-label">{item.label || `Case ${index + 1}`}</span>
						<span class="case-value">{caseValueLabel(item)}</span>
					</div>
				{/each}
				<div class="default-row" data-switch-port="default">
					<i class="fa-solid fa-ellipsis"></i>
					<span>Default</span>
				</div>
			</div>
		{:else}
			<span class="empty-hint">Add cases in the inspector</span>
		{/if}
	</div>
</BaseNode>

<style>
	.switch-summary {
		display: flex;
		flex-direction: column;
		gap: 0.4rem;
		min-width: 14rem;
	}

	.source-row,
	.case-row,
	.default-row {
		display: flex;
		align-items: center;
		gap: 0.4rem;
	}

	.source-row {
		padding: 0.3rem 0.5rem;
		border: 1px solid var(--flow-chip-border);
		border-radius: 0.4rem;
		color: var(--flow-slot-strong);
		font-size: 0.75rem;
	}

	.source-row span {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.unconfigured-source,
	.empty-hint {
		color: var(--color-text-disabled, #64748b);
		font-style: italic;
	}

	.case-list {
		display: flex;
		flex-direction: column;
		gap: 0.2rem;
	}

	.case-row {
		display: grid;
		grid-template-columns: 1.35rem minmax(0, 1fr) auto;
		min-height: 1.75rem;
		padding: 0.25rem 0.55rem 0.25rem 0.35rem;
		border: 1px solid color-mix(in srgb, var(--switch-case-color) 42%, transparent);
		border-left: 3px solid var(--switch-case-color);
		border-radius: 0.4rem;
		background: color-mix(in srgb, var(--switch-case-color) 10%, transparent);
		font-size: 0.75rem;
	}

	.case-index {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 1.15rem;
		height: 1.15rem;
		border-radius: 50%;
		background: color-mix(in srgb, var(--switch-case-color) 24%, transparent);
		color: var(--switch-case-color);
		font-size: 0.65rem;
		font-weight: 800;
	}

	.case-label {
		overflow: hidden;
		color: var(--flow-slot-strong);
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.case-value {
		flex-shrink: 0;
		color: var(--switch-case-color);
		font-family: monospace;
		font-weight: 700;
	}

	.default-row {
		min-height: 1.65rem;
		padding: 0.25rem 0.55rem;
		border: 1px dashed var(--flow-chip-border);
		border-radius: 0.4rem;
		background: rgba(148, 163, 184, 0.05);
		color: var(--flow-slot-muted, #64748b);
		font-size: 0.72rem;
		font-weight: 600;
		font-style: italic;
	}

	.empty-hint {
		font-size: 0.72rem;
	}
</style>
